import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, ArrowRight, Phone, Home as HomeIcon, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import VacancyCard from "../components/VacancyCard";
import VideoSection from "../components/VideoSection";
import FeaturedHome from "../components/FeaturedHome";
import { PROPERTIES, statusBadge } from "../data/properties";
import { VIDEOS } from "../data/videos";
import { useSEO, breadcrumbSchema } from "../lib/seo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { trackPhoneClick } from "../lib/tracking";

const ACCESS_FEATURES = [
  "Step-free entry",
  "Wheelchair accessible kitchen",
  "Accessible bathroom",
  "Wide doorways",
  "Ceiling hoist ready",
  "Roll-in shower",
];

const SDA_CATEGORIES = [
  "Improved Liveability",
  "Fully Accessible",
  "Robust",
  "High Physical Support",
];

const BEDROOM_OPTS = [
  { v: "", label: "Any" },
  { v: "1", label: "1+" },
  { v: "2", label: "2+" },
  { v: "3", label: "3+" },
  { v: "4", label: "4+" },
];

function Filters({ facets, filters, setFilters, clear }) {
  const setK = (k) => (v) => setFilters((f) => ({ ...f, [k]: v === "any" || v === "" ? "" : v }));
  const toggleAccess = (feat) => {
    const cur = new Set(filters.accessibility);
    if (cur.has(feat)) cur.delete(feat); else cur.add(feat);
    setFilters((f) => ({ ...f, accessibility: [...cur] }));
  };
  return (
    <div className="space-y-6" data-testid="vacancy-filters">
      <div>
        <label className="alpha-overline">Search</label>
        <div className="relative mt-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--alpha-mute)]" />
          <Input
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Suburb, title, keyword"
            className="pl-9 h-11"
            data-testid="filter-search"
          />
        </div>
      </div>

      <div>
        <label className="alpha-overline">State</label>
        <Select value={filters.state || "any"} onValueChange={setK("state")}>
          <SelectTrigger className="mt-2 h-11" data-testid="filter-state"><SelectValue placeholder="Any state" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any state</SelectItem>
            {(facets?.states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="alpha-overline">Suburb</label>
        <Input
          className="mt-2 h-11"
          value={filters.suburb}
          onChange={(e) => setFilters((f) => ({ ...f, suburb: e.target.value }))}
          placeholder="e.g. Parramatta"
          data-testid="filter-suburb"
        />
      </div>

      <div>
        <label className="alpha-overline">Service type</label>
        <Select value={filters.service_type || "any"} onValueChange={setK("service_type")}>
          <SelectTrigger className="mt-2 h-11" data-testid="filter-service"><SelectValue placeholder="Any type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any type</SelectItem>
            {(facets?.service_types || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="alpha-overline">SDA design category</label>
        <Select value={filters.sda_category || "any"} onValueChange={setK("sda_category")}>
          <SelectTrigger className="mt-2 h-11" data-testid="filter-sda-category"><SelectValue placeholder="Any SDA category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any SDA category</SelectItem>
            {SDA_CATEGORIES.map((s) => (
              <SelectItem key={s} value={s} disabled={facets?.sda_categories && !facets.sda_categories.includes(s)}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="mt-1.5 text-[11px] text-[color:var(--alpha-mute)]">Only applies to SDA listings.</p>
      </div>

      <div>
        <label className="alpha-overline">Bedrooms</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {BEDROOM_OPTS.map((b) => (
            <button
              key={b.label}
              onClick={() => setFilters((f) => ({ ...f, bedrooms: b.v }))}
              className={`alpha-chip cursor-pointer ${filters.bedrooms === b.v ? "!bg-[color:var(--alpha-purple-800)] !text-white !border-[color:var(--alpha-purple-800)]" : ""}`}
              data-testid={`filter-bed-${b.label}`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="alpha-overline">Availability</label>
        <Select value={filters.availability || "any"} onValueChange={setK("availability")}>
          <SelectTrigger className="mt-2 h-11" data-testid="filter-avail"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            <SelectItem value="available">Available now</SelectItem>
            <SelectItem value="limited">Limited rooms</SelectItem>
            <SelectItem value="waitlist">Join waitlist</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="alpha-overline">Accessibility features</label>
        <div className="mt-3 space-y-2">
          {ACCESS_FEATURES.map((f) => (
            <label key={f} className="flex items-center gap-3 text-sm cursor-pointer">
              <Checkbox
                checked={filters.accessibility.includes(f)}
                onCheckedChange={() => toggleAccess(f)}
                data-testid={`filter-access-${f.toLowerCase().replace(/\s+/g, "-")}`}
              />
              <span>{f}</span>
            </label>
          ))}
        </div>
      </div>

      <button onClick={clear} className="text-sm font-semibold text-[color:var(--alpha-purple-700)] inline-flex items-center gap-1" data-testid="filter-clear">
        <X className="w-3.5 h-3.5" /> Clear all filters
      </button>
    </div>
  );
}

export default function VacanciesPage() {
  useSEO({
    title: "SIL & SDA Vacancies Melbourne · Victoria & Perth | Alpha Community Care",
    description: "Browse premium SIL, SDA, STA and MTA vacancies across Melbourne, Victoria and Perth. Filter by state, suburb, service type, SDA design category and accessibility features. 1:1 and 1:2 support only.",
    keywords: [
      "SIL vacancies Melbourne", "SIL vacancies Victoria", "SIL vacancies Perth",
      "SDA vacancies Melbourne", "SDA Victoria", "SDA Perth",
      "NDIS housing Melbourne", "NDIS housing Victoria",
      "Specialist Disability Accommodation Melbourne",
      "Supported Independent Living Melbourne",
      "NDIS vacancy", "NDIS provider Melbourne",
    ],
    canonical: "https://alphacommunitycare.com.au/vacancies",
    jsonLd: [breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Our Homes", path: "/vacancies" },
    ])],
  });
  const [sp, setSp] = useSearchParams();
  const [filters, setFilters] = useState({
    q: sp.get("q") || "",
    state: sp.get("state") || "",
    suburb: sp.get("suburb") || "",
    service_type: sp.get("service_type") || "",
    sda_category: sp.get("sda_category") || "",
    bedrooms: sp.get("bedrooms") || "",
    availability: sp.get("availability") || "",
    accessibility: sp.get("accessibility") ? sp.get("accessibility").split(",") : [],
  });
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(true);

  const qs = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v) ? v.length : v) p[k] = Array.isArray(v) ? v.join(",") : v;
    });
    return p;
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSp(qs, { replace: true });
      setLoading(true);
      api
        .get("/vacancies", { params: qs })
        .then((r) => {
          setItems(r.data.items || []);
          setFacets(r.data.facets);
        })
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(qs)]);

  const clear = () => setFilters({ q: "", state: "", suburb: "", service_type: "", sda_category: "", bedrooms: "", availability: "", accessibility: [] });

  return (
    <div data-testid="page-vacancies">
      {/* Cinematic dark hero */}
      <section className="relative overflow-hidden text-white">
        <div className="absolute inset-0">
          <img
            src={PROPERTIES[0].images[0].src}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover alpha-ken-burns"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0625]/55 via-[#0F0625]/70 to-[#0F0625]/95" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1B0B3D]/65 via-transparent to-[#4FA56E]/10 mix-blend-multiply" />
        <div className="alpha-flow-lines" aria-hidden />
        <div className="alpha-particles" aria-hidden />
        <div className="alpha-grain-dark" aria-hidden />
        <div className="absolute -left-20 top-10 w-[420px] h-[420px] rounded-full pointer-events-none alpha-glow-a" style={{ background: "radial-gradient(closest-side, rgba(134,89,199,0.55), transparent 70%)", filter: "blur(50px)" }} aria-hidden />
        <div className="absolute -right-20 bottom-10 w-[420px] h-[420px] rounded-full pointer-events-none alpha-glow-b" style={{ background: "radial-gradient(closest-side, rgba(79,165,110,0.45), transparent 70%)", filter: "blur(50px)" }} aria-hidden />

        <div className="relative alpha-container pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8 alpha-fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full alpha-glass-dark text-white text-[11px] uppercase tracking-[0.22em] font-semibold">
                <HomeIcon className="w-3.5 h-3.5" /> Our homes · live vacancies
              </div>
              <h1 className="mt-5 font-display font-medium tracking-tight leading-[0.98] !text-white alpha-press"
                  style={{ fontSize: "clamp(34px, 5.4vw, 68px)" }}>
                Find a home that fits — <span className="alpha-serif-italic alpha-shimmer-text">not the other way around</span>.
              </h1>
              <p className="mt-4 max-w-2xl text-white/85 text-[15.5px] md:text-base alpha-press">
                SIL, SDA, STA &amp; MTA homes across Melbourne &amp; Perth. Filter like real estate — property type, SDA design category, accessibility, state, suburb.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end gap-2.5 flex-wrap">
              <Link to="/referrals" className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-3.5 font-semibold inline-flex items-center justify-center gap-2 text-sm hover:scale-[1.02] transition-transform" data-testid="vac-hero-refer">
                Start a referral <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "vacancies-hero")} className="alpha-glass-dark border border-white/30 text-white rounded-full px-6 py-3.5 font-semibold inline-flex items-center justify-center gap-2 text-sm hover:bg-white/15 transition-colors">
                <Phone className="w-4 h-4" /> 1300 380 692
              </a>
            </div>
          </div>
          <div className="mt-7 flex flex-wrap gap-2">
            {[
              { Icon: ShieldCheck, label: "NDIS Registered" },
              { Icon: HomeIcon, label: "SIL · SDA · STA · MTA" },
              { Icon: HomeIcon, label: "4 SDA categories" },
              { Icon: HomeIcon, label: "Melbourne · Perth hubs" },
            ].map((c) => (
              <span key={c.label} className="alpha-glass-dark text-white !py-1.5 !px-3 !text-[11.5px] rounded-full inline-flex items-center gap-1.5 border border-white/15">
                <c.Icon className="w-3.5 h-3.5 text-[color:var(--alpha-green-500)]" /> {c.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile filters trigger */}
      <div className="lg:hidden sticky top-[112px] z-30 bg-white/90 backdrop-blur border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container py-3 flex items-center justify-between">
          <div className="text-sm text-[color:var(--alpha-mute)]">{loading ? "Loading…" : `${items.length} homes`}</div>
          <Sheet>
            <SheetTrigger asChild>
              <button className="alpha-btn-ghost text-sm" data-testid="mobile-filters-btn"><SlidersHorizontal className="w-4 h-4" /> Filters</button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] overflow-y-auto">
              <div className="py-4">
                <Filters facets={facets} filters={filters} setFilters={setFilters} clear={clear} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <section className="alpha-container py-10 grid lg:grid-cols-12 gap-10">
        <aside className="hidden lg:block lg:col-span-3 sticky top-32 self-start">
          <div className="alpha-card p-6">
            <Filters facets={facets} filters={filters} setFilters={setFilters} clear={clear} />
          </div>
        </aside>

        <div className="lg:col-span-9">
          <div className="hidden lg:flex items-center justify-between mb-6">
            <div className="text-sm text-[color:var(--alpha-mute)]" data-testid="vacancy-count">
              {loading ? "Loading…" : `${items.length} home${items.length === 1 ? "" : "s"} matching your filters`}
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={`skeleton-${i}`} className="alpha-card overflow-hidden">
                  <div className="h-56 bg-[color:var(--alpha-soft)] animate-pulse" />
                  <div className="p-5 space-y-2">
                    <div className="h-4 w-3/4 bg-[color:var(--alpha-soft)] animate-pulse rounded" />
                    <div className="h-3 w-1/2 bg-[color:var(--alpha-soft)] animate-pulse rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="alpha-card p-12 text-center" data-testid="no-results">
              <div className="font-display text-xl font-semibold">No homes match those filters</div>
              <p className="text-[color:var(--alpha-mute)] mt-2">Try expanding your state or removing a filter.</p>
              <button onClick={clear} className="alpha-btn-ghost mt-5 text-sm">Clear filters</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="vacancy-grid">
              {/* Pinned: real Alpha properties (Coburg + Aintree …) */}
              {PROPERTIES.map((pp) => {
                const sb = statusBadge(pp);
                return (
                  <Link
                    key={pp.slug}
                    to={pp.href}
                    className="group relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all bg-white border border-[color:var(--alpha-line)] flex flex-col"
                    data-testid={`vacancy-card-${pp.slug}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={pp.images[0].src} alt={pp.images[0].alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#2F1A5C]/55 via-transparent to-transparent" />
                      <span className={`absolute top-3 left-3 alpha-chip !py-1 !px-2.5 !text-[10.5px] font-semibold uppercase tracking-[0.18em] ${
                        sb.tone === "green"
                          ? "!bg-[color:var(--alpha-green-600)] !text-white !border-[color:var(--alpha-green-700)]"
                          : sb.tone === "purple"
                          ? "!bg-[color:var(--alpha-purple-700)] !text-white !border-[color:var(--alpha-purple-800)]"
                          : "!bg-white !text-[color:var(--alpha-ink)] !border-white"
                      }`}>
                        Featured · {sb.label}
                      </span>
                      <span className="absolute top-3 right-3 alpha-chip !bg-white !text-[color:var(--alpha-purple-700)] !border-white !py-1 !px-2.5 !text-[11px] font-semibold">
                        1:1 & 1:2 only
                      </span>
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="font-display font-semibold text-lg leading-snug group-hover:text-[color:var(--alpha-purple-700)] transition-colors">{pp.name}</h3>
                      <div className="mt-1 text-sm text-[color:var(--alpha-mute)]">{pp.suburb}, {pp.state} · {pp.short.split(' · ')[0]}</div>
                      <p className="mt-2 text-sm text-[color:var(--alpha-mute)] line-clamp-2">{pp.oneLiner}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {pp.features.slice(0, 3).map((f) => (
                          <span key={f} className="alpha-chip alpha-chip-neutral !text-[11px] !py-1 !px-2">{f}</span>
                        ))}
                      </div>
                      <div className="mt-auto pt-4 flex items-center justify-between border-t border-[color:var(--alpha-line)]/70 mt-4">
                        <span className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--alpha-mute)] font-semibold">View home</span>
                        <span className="text-[color:var(--alpha-purple-700)] font-semibold group-hover:translate-x-1 transition-transform">→</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
              {items.map((v) => <VacancyCard key={v.id} vacancy={v} />)}
            </div>
          )}
        </div>
      </section>

      {/* Step-inside video — support workers & homes */}
      <VideoSection
        src={VIDEOS["support-worker"].url}
        eyebrow="Take a tour · Support workers & homes"
        title="Step inside an Alpha home."
        subtitle={VIDEOS["support-worker"].blurb}
        testid="vac-video-tour"
      />

      {/* Featured Home — cinematic dark band */}
      <FeaturedHome variant="compact" testid="vac-featured-home" />      {/* Final CTA strip */}
      <section className="alpha-section-tight">
        <div className="alpha-container">
          <div className="relative overflow-hidden rounded-[24px] md:rounded-[32px] alpha-gradient p-8 md:p-12 text-white alpha-noise">
            <div className="alpha-orb alpha-orb-green w-[260px] h-[260px] right-[-40px] top-[-40px] opacity-60 hidden md:block" aria-hidden />
            <div className="relative grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[11px] font-semibold uppercase tracking-[0.22em]">
                  <HomeIcon className="w-3.5 h-3.5" /> Didn't find the right fit?
                </div>
                <h2 className="mt-4 font-display text-3xl md:text-[40px] font-medium leading-[1.05] !text-white">
                  New homes open <span className="alpha-serif-italic">monthly</span>.
                </h2>
                <p className="mt-3 text-white/85 text-[15px]">Tell us what you need and we'll match you — before it goes live.</p>
              </div>
              <div className="md:col-span-5 flex flex-col sm:flex-row md:flex-col gap-2.5">
                <Link to="/referrals" className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-4 font-semibold text-center hover:bg-[color:var(--alpha-purple-50)] transition inline-flex items-center justify-center gap-2" data-testid="vac-cta-refer">
                  Start a referral <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "vacancies-cta")} className="border-2 border-white/50 rounded-full px-6 py-4 font-semibold text-center hover:bg-white/10 transition inline-flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" /> 1300 380 692
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
